import { Box, Typography } from '@mui/material';
import { formatValue } from './format';
import { awayDashOf } from './dash';
import { candleAt, readoutRows } from './panelData';
import { SeriesSwatch } from './SeriesSwatch';
import type { ChartSeries, PanelId } from './seriesTypes';

const TOOLTIP_WIDTH = 230;

interface CursorTooltipProps {
    series: readonly ChartSeries[];
    /** The panel the rows are drawn in: a line away from home is marked. */
    panelId: PanelId;
    time: number;
    x: number;
    top: number;
    panelWidth: number;
}

export function CursorTooltip({
    series,
    panelId,
    time,
    x,
    top,
    panelWidth,
}: CursorTooltipProps) {
    const rows = readoutRows(series, time);
    if (rows.length === 0) return null;
    // A dashed swatch is wider than a square one. With both in one tooltip
    // every swatch gets the same slot, so the labels start at the same x.
    const mixed = rows.some(r => awayDashOf(r.series, panelId) !== undefined);
    const onRight = x + 16 + TOOLTIP_WIDTH < panelWidth;

    return (
        <Box
            data-testid="cursor-tooltip"
            sx={{
                position: 'absolute',
                left: onRight ? x + 16 : Math.max(4, x - 16 - TOOLTIP_WIDTH),
                top,
                width: TOOLTIP_WIDTH,
                p: 1,
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: 3,
                pointerEvents: 'none',
                zIndex: 2,
            }}
        >
            <Typography
                sx={{ fontSize: '10px', color: 'text.disabled', mb: 0.5 }}
            >
                {new Date(time).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                })}
            </Typography>
            {rows.map(({ series: s, value }) => {
                const candle =
                    s.data.kind === 'candles'
                        ? candleAt(s.data.candles, time)
                        : null;
                return (
                    <Box key={s.id}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                            }}
                        >
                            <SeriesSwatch
                                series={s}
                                panelId={panelId}
                                slot={mixed}
                            />
                            <Typography
                                sx={{
                                    fontSize: '11px',
                                    color: 'text.secondary',
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {s.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '11px',
                                    fontFamily: 'monospace',
                                    fontWeight: 600,
                                }}
                            >
                                {formatValue(s.unit, value)}
                            </Typography>
                        </Box>
                        {candle && (
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    fontFamily: 'monospace',
                                    color: 'text.disabled',
                                    pl: '14px',
                                }}
                            >
                                O {formatValue(s.unit, candle.open)} H{' '}
                                {formatValue(s.unit, candle.high)} L{' '}
                                {formatValue(s.unit, candle.low)}
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
